import React, { useState, useRef, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import axios from "axios";
import { SERVICE_LOCATION, GET_MEASURES_WITH_PAYMENT_CARD_REQUIRED_STATUS } from "../constants/endpoints";

import "./../assets/styles/table.css";
import "./../assets/styles/fitter.css";
import "./../assets/styles/reports.css";

const PaymentCardRequiredReport = (props) => {
    const { data } = props;

    const toast = useRef(null);

    const [paymentCardRequiredReportData, setPaymentCardRequiredReportData] = useState(null);
    const [filteredReportData, setFilteredReportData] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState("");

    useEffect(() => {
        const currentDate = new Date();
        setSelectedDate(currentDate);
        getPaymentCardRequiredData(currentDate);
        initFilters();
    }, []);

    const getFormattedDate = (dateToFormat) => {
        const year = dateToFormat.getFullYear();
        const month = String(dateToFormat.getMonth() + 1).padStart(2, "0");
        const day = String(dateToFormat.getDate()).padStart(2, "0");
        const formatedDate = `${year}-${month}-${day}`;
        return formatedDate;
    }

    const getPaymentCardRequiredData = (date) => {
        setLoading(true);
        const formattedDate = getFormattedDate(date);
        const requestedData = {
            SelectedDate: formattedDate
        }

        const endPoint = SERVICE_LOCATION + GET_MEASURES_WITH_PAYMENT_CARD_REQUIRED_STATUS;
        axios.post(endPoint, requestedData, {
            headers: {
                Authorization: `Bearer ${data.data.token}`,
            },
        })
            .then((response) => {
                setLoading(false);
                if (response.data.isSuccess) {
                    const paymentCardRequiredData = response.data.data.paymentCardRequiredMeasures;
                    setPaymentCardRequiredReportData(paymentCardRequiredData);
                    setFilteredReportData(paymentCardRequiredData);
                }
                else if (!response.data.isSuccess) {
                    setPaymentCardRequiredReportData(null);
                    setFilteredReportData(null);
                    toast.current.show({ severity: "error", summary: "Error", detail: response.data.errorMessage, life: 3000 });
                }
            })
            .catch((error) => {
                setPaymentCardRequiredReportData(null);
                setFilteredReportData(null);
                toast.current.show({ severity: "error", summary: "Error", detail: error, life: 3000 });
            })
    };

    const handleSelectedDateChange = (e) => {
        const dateSelected = e.value;
        setSelectedDate(dateSelected);
        getPaymentCardRequiredData(dateSelected);
    }

    const getFilteredReportData = (filteredData) => {
        setFilteredReportData(filteredData);
    }

    //export functionality
    const saveAsExcelFile = (buffer, fileName) => {
        import("file-saver").then((module) => {
            if (module && module.default) {
                let EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
                let EXCEL_EXTENSION = ".xlsx";
                const data = new Blob([buffer], {
                    type: EXCEL_TYPE
                });

                module.default.saveAs(data, fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION);
            }
        });
    };

    const exportExcel = () => {
        import("xlsx").then((xlsx) => {
            const worksheet = xlsx.utils.json_to_sheet(filteredReportData);
            const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
            const excelBuffer = xlsx.write(workbook, {
                bookType: "xlsx",
                type: "array"
            });

            saveAsExcelFile(excelBuffer, "PaymentCardRequiredReport");
        });
    };
    //end export functionality

    // Search functionality
    const clearFilter = () => {
        initFilters();
    };

    const initFilters = () => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            measureId: { value: null, matchMode: FilterMatchMode.CONTAINS },
            measureDescription: { value: null, matchMode: FilterMatchMode.CONTAINS },
            measureType: { value: null, matchMode: FilterMatchMode.CONTAINS },
            fitter: { value: null, matchMode: FilterMatchMode.CONTAINS },
            depot: { value: null, matchMode: FilterMatchMode.CONTAINS },
            client: { value: null, matchMode: FilterMatchMode.CONTAINS },
            site: { value: null, matchMode: FilterMatchMode.CONTAINS },
            measureQTY: { value: null, matchMode: FilterMatchMode.CONTAINS },
            paymentCardRequired: { value: null, matchMode: FilterMatchMode.CONTAINS }
        });
        setGlobalFilterValue("");
    }

    const renderHeader = () => {
        return (
            <div className="row">
                <div className="col-12 col-sm-3 col-md-3 col-lg-3 col-xl-3">
                    <div className="flex align-items-center justify-content-end gap-2">
                        <Button className="mar-r-10" type="button" icon="pi pi-file-excel" severity="success" onClick={exportExcel} rounded tooltip="Export Report" />
                    </div>
                </div>
                <div className="col-12 col-sm-9 col-md-9 col-lg-9 col-xl-9">
                    <div className="flex justify-content-end txt-right">
                        <Button className="mar-r-10" type="button" icon="pi pi-filter-slash" label="Clear" outlined onClick={clearFilter} tooltip="clear filters" tooltipOptions={{ position: "bottom" }} />
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
                        </span>
                    </div>
                </div>

            </div>
        );
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters["global"].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const header = renderHeader();
    // Search functionality Ends here

    return (
        <div className={`app ${loading ? "disabled" : ""}`}>
            {
                loading &&
                <div className="spinner-overlay">
                    <ProgressSpinner style={{ width: "50px", height: "50px" }} strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" />
                </div>
            }

            <Toast ref={toast} />

            <div className="worksheets-section padl-20">
                <h4>Payment Cards Required Report</h4>
                <div className="row pad-tb-10">
                    <div className="col-3">
                        <label htmlFor="select-date">Select Date</label>
                        <Calendar id="select-date" name="select-date" value={selectedDate} onChange={handleSelectedDateChange} showIcon dateFormat="dd/mm/yy" />
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <DataTable className="font-12" value={paymentCardRequiredReportData} dataKey="measureId" paginator rows={10}
                            rowsPerPageOptions={[10, 25, 50]} filters={filters} header={header} filterDisplay="row" onValueChange={getFilteredReportData}
                            globalFilterFields={["measureId", "measureDescription", "measureType", "fitter", "depot", "client",
                                "site", "measureQTY", "paymentCardRequired"]}
                            scrollable tableStyle={{ minWidth: "100rem" }} emptyMessage="No measures availabe for the selected date">
                            <Column field="measureId" key="measureId" header="Measure Id" filter filterPlaceholder="Search by measureId" className="table-header"></Column>
                            <Column field="measureDescription" header="Measure Description" filter filterPlaceholder="Search by description" className="table-header" ></Column>
                            <Column field="measureType" header="Measure Type" filter filterPlaceholder="Search by measuretype" className="table-header" ></Column>
                            <Column field="fitter" header="Fitter" filter filterPlaceholder="Search by fitter" className="table-header"></Column>
                            <Column field="depot" header="Depot" filter filterPlaceholder="Search by depot" className="table-header"></Column>
                            <Column field="client" header="Client" filter filterPlaceholder="Search by client" className="table-header"></Column>
                            <Column field="site" header="Site" filter filterPlaceholder="Search by site" className="table-header"></Column>
                            <Column field="measureQTY" header="Measure QTY" filter filterPlaceholder="Search by quantity" className="table-header"></Column>
                            <Column field="paymentCardRequired" header="PaymentCardRequired" filter filterPlaceholder="Search by paymentcard required" className="table-header"></Column>
                        </DataTable>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PaymentCardRequiredReport;